export const STATUS = Object.freeze({
  DADO_INCONSISTENTE: 'DADO_INCONSISTENTE',
  NAO_COMPARECE: 'NAO_COMPARECE',
  FREQUENTE: 'FREQUENTE',
  IRREGULAR: 'IRREGULAR',
  COMPARECE: 'COMPARECE',
  NAO_INFORMADO: 'NAO_INFORMADO',
});

export const STATUS_CONFIG = Object.freeze({
  [STATUS.FREQUENTE]: {
    label: 'Frequente',
    shortLabel: 'F',
    color: '#15803d',
    lightColor: '#dcfce7',
    group: 'presenca',
    order: 0,
  },
  [STATUS.COMPARECE]: {
    label: 'Comparece',
    shortLabel: 'C',
    color: '#16a34a',
    lightColor: '#f0fdf4',
    group: 'presenca',
    order: 1,
  },
  [STATUS.IRREGULAR]: {
    label: 'Irregular',
    shortLabel: 'I',
    color: '#f59e0b',
    lightColor: '#fffbeb',
    group: 'atencao',
    order: 2,
  },
  [STATUS.NAO_COMPARECE]: {
    label: 'Não comparece',
    shortLabel: 'N',
    color: '#dc2626',
    lightColor: '#fef2f2',
    group: 'atencao',
    order: 3,
  },
  [STATUS.NAO_INFORMADO]: {
    label: 'Não informado',
    shortLabel: '?',
    color: '#64748b',
    lightColor: '#f1f5f9',
    group: 'sem_dados',
    order: 4,
  },
  [STATUS.DADO_INCONSISTENTE]: {
    label: 'Dado inconsistente',
    shortLabel: '!',
    color: '#7c3aed',
    lightColor: '#f5f3ff',
    group: 'inconsistente',
    order: 5,
  },
});

export const STATUS_OPTIONS = Object.values(STATUS).map((value) => ({
  value,
  ...STATUS_CONFIG[value],
}));

export const TRI_STATE_OPTIONS = Object.freeze([
  { value: '', label: 'Todos' },
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
  { value: 'null', label: 'Não informado' },
]);

const collator = new Intl.Collator('pt-BR', { sensitivity: 'base', numeric: true });

export function normalizarTexto(value) {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function paraTriState(value) {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  if (value === null || value === 'null') return null;
  return undefined;
}

export function formatarTriState(value) {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return 'Não informado';
}

export function temCoordenadas(membro) {
  const latRaw = membro?.lat ?? membro?.latitude;
  const lngRaw = membro?.lng ?? membro?.longitude;
  if (latRaw === null || latRaw === undefined || latRaw === '' ||
      lngRaw === null || lngRaw === undefined || lngRaw === '') return false;
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

export function coordenadasDoMembro(membro) {
  if (!temCoordenadas(membro)) return null;
  return {
    lat: Number(membro.lat ?? membro.latitude),
    lng: Number(membro.lng ?? membro.longitude),
  };
}

export function calcularStatusLocal(membro = {}) {
  const comparece = paraTriState(membro.comparece_reuniao);
  const frequente = paraTriState(membro.frequente_reuniao);
  if (comparece === false && frequente === true) return STATUS.DADO_INCONSISTENTE;
  if (comparece === false) return STATUS.NAO_COMPARECE;
  if (frequente === true) return STATUS.FREQUENTE;
  if (frequente === false) return STATUS.IRREGULAR;
  if (comparece === true) return STATUS.COMPARECE;
  return STATUS.NAO_INFORMADO;
}

export function statusDoMembro(membro = {}) {
  return STATUS_CONFIG[membro.status_frequencia]
    ? membro.status_frequencia
    : calcularStatusLocal(membro);
}

export function getStatusConfig(status) {
  return STATUS_CONFIG[status] || STATUS_CONFIG[STATUS.NAO_INFORMADO];
}

function atendeTriState(valor, filtro) {
  const esperado = paraTriState(filtro);
  return esperado === undefined || valor === esperado;
}

export function filtrarMembros(membros = [], filtros = {}) {
  const categorias = filtros.categorias?.length ? filtros.categorias : ['crianca', 'jovem'];
  const status = filtros.status || [];
  const busca = normalizarTexto(filtros.busca);
  const bairro = normalizarTexto(filtros.bairro);
  const batizado = paraTriState(filtros.batizado);

  return membros.filter((membro) => {
    if (!categorias.includes(membro.categoria)) return false;
    if (status.length && !status.includes(statusDoMembro(membro))) return false;
    if (bairro && normalizarTexto(membro.bairro) !== bairro) return false;
    if (
      busca &&
      !normalizarTexto([
        membro.nome,
        membro.endereco,
        membro.bairro,
        membro.responsavel,
      ].filter(Boolean).join(' ')).includes(busca)
    ) return false;
    if (filtros.tipo_pessoa && membro.tipo_pessoa !== filtros.tipo_pessoa) return false;
    if (filtros.localizacao === 'com' && !temCoordenadas(membro)) return false;
    if (filtros.localizacao === 'sem' && temCoordenadas(membro)) return false;
    // Ao exibir crianças e jovens juntos, o filtro de batismo restringe somente jovens.
    if (batizado !== undefined && membro.categoria === 'jovem' && membro.batizado !== batizado) return false;
    if (!atendeTriState(membro.visita_indicada, filtros.visita_indicada)) return false;
    if (!atendeTriState(membro.aceitou_visita, filtros.aceitou_visita)) return false;
    return true;
  });
}

export function ordenarMembros(membros = [], criterio = 'status') {
  return [...membros].sort((a, b) => {
    if (criterio === 'nome') return collator.compare(a.nome || '', b.nome || '');
    if (criterio === 'bairro') {
      return collator.compare(a.bairro || '', b.bairro || '') ||
        collator.compare(a.nome || '', b.nome || '');
    }
    if (criterio === 'idade') {
      return (Number(a.idade ?? -1) - Number(b.idade ?? -1)) ||
        collator.compare(a.nome || '', b.nome || '');
    }
    return (
      getStatusConfig(statusDoMembro(a)).order - getStatusConfig(statusDoMembro(b)).order ||
      collator.compare(a.nome || '', b.nome || '')
    );
  });
}

function percentual(numerador, denominador) {
  return denominador > 0 ? Math.round((numerador / denominador) * 1000) / 10 : null;
}

export function calcularResumo(membros = []) {
  const contagem = Object.fromEntries(Object.values(STATUS).map((status) => [status, 0]));
  membros.forEach((membro) => { contagem[statusDoMembro(membro)] += 1; });
  const total = membros.length;
  const frequentes = contagem[STATUS.FREQUENTE];
  const comparecem = contagem[STATUS.COMPARECE];
  const irregulares = contagem[STATUS.IRREGULAR];
  const naoComparecem = contagem[STATUS.NAO_COMPARECE];
  const naoInformados = contagem[STATUS.NAO_INFORMADO];
  const inconsistentes = contagem[STATUS.DADO_INCONSISTENTE];
  const conhecidos = total - naoInformados - inconsistentes;
  const presenca = frequentes + comparecem;
  const atencao = irregulares + naoComparecem;
  const comCoordenadas = membros.filter(temCoordenadas).length;
  const jovens = membros.filter((membro) => membro.categoria === 'jovem');

  return {
    total,
    com_coordenadas: comCoordenadas,
    sem_coordenadas: total - comCoordenadas,
    com_dados_conhecidos: conhecidos,
    frequentes,
    comparecem,
    irregulares,
    nao_comparecem: naoComparecem,
    nao_informados: naoInformados,
    dados_inconsistentes: inconsistentes,
    presenca,
    quantidade_atencao: atencao,
    taxa_comparecimento: percentual(presenca, conhecidos),
    taxa_atencao: percentual(atencao, conhecidos),
    cobertura_dados: percentual(conhecidos, total),
    cobertura_coordenadas: percentual(comCoordenadas, total),
    jovens_batizados: jovens.filter((membro) => membro.batizado === true).length,
    jovens_nao_batizados: jovens.filter((membro) => membro.batizado === false).length,
    jovens_batismo_nao_informado: jovens.filter((membro) => membro.batizado == null).length,
    por_status: contagem,
  };
}

export function construirRegioes(membros = []) {
  const grupos = membros.reduce((acc, membro) => {
    const bairro = membro.bairro || 'sem endereço';
    if (!acc.has(bairro)) acc.set(bairro, []);
    acc.get(bairro).push(membro);
    return acc;
  }, new Map());

  return Array.from(grupos, ([bairro, pessoas]) => {
    const resumo = calcularResumo(pessoas);
    const localizados = pessoas.map(coordenadasDoMembro).filter(Boolean);
    const centroide = localizados.length
      ? {
          lat: localizados.reduce((sum, item) => sum + item.lat, 0) / localizados.length,
          lng: localizados.reduce((sum, item) => sum + item.lng, 0) / localizados.length,
        }
      : null;
    return {
      bairro,
      ...resumo,
      com_dados_conhecidos: resumo.com_dados_conhecidos,
      quantidade_atencao: resumo.quantidade_atencao,
      centroide,
    };
  }).sort((a, b) => (
    b.quantidade_atencao - a.quantidade_atencao ||
    b.total - a.total ||
    collator.compare(a.bairro, b.bairro)
  ));
}

export function gerarInsights(regioes = [], resumo = calcularResumo([]), minimoConhecidos = 3) {
  const elegiveis = regioes.filter((regiao) => regiao.com_dados_conhecidos >= minimoConhecidos);
  const menorTaxa = [...elegiveis].sort((a, b) => (
    (a.taxa_comparecimento ?? Infinity) - (b.taxa_comparecimento ?? Infinity) ||
    b.quantidade_atencao - a.quantidade_atencao
  ))[0] || null;
  const maiorAtencao = [...elegiveis].sort((a, b) => (
    b.quantidade_atencao - a.quantidade_atencao ||
    (b.taxa_atencao ?? -1) - (a.taxa_atencao ?? -1)
  ))[0] || null;

  return {
    menor_taxa: menorTaxa ? {
      bairro: menorTaxa.bairro,
      taxa_comparecimento: menorTaxa.taxa_comparecimento,
      conhecidos: menorTaxa.com_dados_conhecidos,
      presenca: menorTaxa.presenca,
      quantidade_atencao: menorTaxa.quantidade_atencao,
    } : null,
    maior_atencao: maiorAtencao ? {
      bairro: maiorAtencao.bairro,
      taxa_atencao: maiorAtencao.taxa_atencao,
      pessoas: maiorAtencao.quantidade_atencao,
      conhecidos: maiorAtencao.com_dados_conhecidos,
    } : null,
    cobertura: {
      dados_percentual: resumo.cobertura_dados,
      coordenadas_percentual: resumo.cobertura_coordenadas,
      conhecidos: resumo.com_dados_conhecidos,
      total: resumo.total,
    },
    minimo_conhecidos_por_bairro: minimoConhecidos,
  };
}

export function formatarData(value, comHora = false) {
  if (!value) return 'Não informado';
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return 'Não informado';
  return data.toLocaleString('pt-BR', comHora
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' });
}

export function construirQueryAnalise(filtros = {}, buscaDebounced = '') {
  const params = new URLSearchParams();
  if (filtros.categorias?.length) params.set('categorias', filtros.categorias.join(','));
  if (filtros.status?.length) params.set('status', filtros.status.join(','));
  if (filtros.bairro) params.set('bairro', filtros.bairro);
  if (buscaDebounced) params.set('busca', buscaDebounced);
  if (filtros.tipo_pessoa) params.set('tipo_pessoa', filtros.tipo_pessoa);
  if (filtros.localizacao === 'com') params.set('somente_com_coordenadas', 'true');
  for (const campo of ['batizado', 'visita_indicada', 'aceitou_visita']) {
    if (filtros[campo] !== '') params.set(campo, filtros[campo]);
  }
  return params;
}

function linhaPessoa(membro) {
  const coords = coordenadasDoMembro(membro);
  return {
    ID: membro.id,
    Nome: membro.nome || '',
    Categoria: membro.categoria === 'crianca' ? 'Criança' : 'Jovem',
    Idade: membro.idade ?? '',
    'Tipo de pessoa': membro.tipo_pessoa === 'irma' ? 'Irmã' : 'Irmão',
    Status: getStatusConfig(statusDoMembro(membro)).label,
    'É frequente?': formatarTriState(membro.frequente_reuniao),
    'Comparece à reunião?': formatarTriState(membro.comparece_reuniao),
    'Batizado?': membro.categoria === 'jovem' ? formatarTriState(membro.batizado) : 'Não se aplica',
    'Visita indicada?': formatarTriState(membro.visita_indicada),
    'Aceitou visita?': formatarTriState(membro.aceitou_visita),
    Bairro: membro.bairro || '',
    Endereço: membro.endereco || '',
    Telefone: membro.telefone || '',
    Responsável: membro.responsavel || '',
    'Telefone do responsável': membro.responsavel_telefone || '',
    Latitude: coords?.lat ?? '',
    Longitude: coords?.lng ?? '',
    'Atualização da frequência': formatarData(membro.frequencia_atualizada_em, true),
  };
}

export function criarDadosExportacao({ membros = [], regioes = [], resumo = {}, filtros = {} }) {
  const pessoas = membros.map(linhaPessoa);
  const resumoLinhas = [
    { Indicador: 'Total', Valor: resumo.total ?? 0 },
    { Indicador: 'Com localização', Valor: resumo.com_coordenadas ?? 0 },
    { Indicador: 'Sem localização', Valor: resumo.sem_coordenadas ?? 0 },
    { Indicador: 'Frequentes', Valor: resumo.frequentes ?? 0 },
    { Indicador: 'Comparecem', Valor: resumo.comparecem ?? 0 },
    { Indicador: 'Irregulares', Valor: resumo.irregulares ?? 0 },
    { Indicador: 'Não comparecem', Valor: resumo.nao_comparecem ?? 0 },
    { Indicador: 'Não informados', Valor: resumo.nao_informados ?? 0 },
    { Indicador: 'Dados inconsistentes', Valor: resumo.dados_inconsistentes ?? 0 },
    { Indicador: 'Taxa de comparecimento', Valor: resumo.taxa_comparecimento == null ? 'Sem amostra' : `${resumo.taxa_comparecimento}%` },
    { Indicador: 'Taxa de atenção', Valor: resumo.taxa_atencao == null ? 'Sem amostra' : `${resumo.taxa_atencao}%` },
    { Indicador: 'Cobertura dos dados', Valor: resumo.cobertura_dados == null ? 'Sem amostra' : `${resumo.cobertura_dados}%` },
    { Indicador: 'Filtros ativos', Valor: JSON.stringify(filtros) },
  ];
  const regioesLinhas = regioes.map((regiao) => ({
    Bairro: regiao.bairro,
    Total: regiao.total,
    'Dados conhecidos': regiao.com_dados_conhecidos,
    Frequentes: regiao.frequentes,
    Comparecem: regiao.comparecem,
    Irregulares: regiao.irregulares,
    'Não comparecem': regiao.nao_comparecem,
    'Não informados': regiao.nao_informados,
    'Dados inconsistentes': regiao.dados_inconsistentes,
    'Quantidade em atenção': regiao.quantidade_atencao,
    'Taxa de comparecimento': regiao.taxa_comparecimento == null ? '' : `${regiao.taxa_comparecimento}%`,
    'Taxa de atenção': regiao.taxa_atencao == null ? '' : `${regiao.taxa_atencao}%`,
    'Cobertura dos dados': regiao.cobertura_dados == null ? '' : `${regiao.cobertura_dados}%`,
    Latitude: regiao.centroide?.lat ?? '',
    Longitude: regiao.centroide?.lng ?? '',
  }));

  return {
    Resumo: resumoLinhas,
    Pessoas: pessoas,
    Regioes: regioesLinhas,
    'Sem localização': membros.filter((membro) => !temCoordenadas(membro)).map(linhaPessoa),
    'Dados inconsistentes': membros
      .filter((membro) => statusDoMembro(membro) === STATUS.DADO_INCONSISTENTE)
      .map(linhaPessoa),
  };
}
